import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MessageCircle, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { CheckRow, CtaBand, Label, PageHero, SectionHead } from "@/components/Shared";
import { Reveal } from "@/components/Reveal";
import { Stadtmusikanten } from "@/components/graphics/Marks";
import { BRAND, IMG } from "@/lib/site";

const crumbs = [
  { label: "Startseite", to: "/" },
  { label: "Über uns", to: "/ueber-uns" },
];

const UeberUns = () => (
  <main data-testid="page-ueber-uns">
    <Seo
      title="Über uns | Bremer Schneemann – Winterdienst Bremen"
      description="Bremer Schneemann: regionaler Winterdienst in Bremen, geführt von Inhaber Adil Altun. Persönlicher Ansprechpartner für private und gewerbliche Objekte."
      path="/ueber-uns"
      breadcrumbs={crumbs}
      image={IMG.oldTownLane}
    />
    <PageHero
      label="/ Über uns"
      titleLines={["Bremer Schneemann –", "regionaler Winterdienst", "mit einem Gesicht."]}
      intro="Hinter Bremer Schneemann steht Adil Altun. Er plant die Einsätze, nimmt die Objekte auf und bleibt über die Saison Ihr Ansprechpartner."
      image={IMG.oldTownLane}
      breadcrumbs={crumbs}
    />


    <section className="bg-white py-16 lg:py-24">
      <div className="shell grid gap-14 lg:grid-cols-[1.15fr_0.85fr] lg:gap-20">
        <div>
          <SectionHead
            label="/ Wer wir sind"
            title="Ein Bremer Betrieb, der Winterdienst als Handwerk versteht."
          />
          <div className="prose-de mt-7">
            <p className="text-[15px] sm:text-base">
              Bremer Schneemann ist ein regionaler Winterdienst mit Hauptbüro an der Flughafenallee 26
              auf der Überseeinsel in 28199 Bremen; der Fuhrpark startet aus Bremen-Hemelingen.
              Inhaber ist Adil Altun. Er übernimmt die Objektaufnahme,
              plant die Touren und ist derjenige, mit dem Sie sprechen – vor der Saison, während
              der Saison und wenn es morgens um fünf schneit.
            </p>
            <p className="text-[15px] sm:text-base">
              Wir arbeiten bewusst regional. Winterdienst lebt davon, dass Flächen zum richtigen
              Zeitpunkt geräumt und gestreut sind, und das funktioniert nur mit kurzen Wegen und
              dichten Touren. Deshalb ist unser Kerngebiet das Bremer Stadtgebiet und das direkt
              angrenzende Umland – nicht eine beliebig große Landkarte.
            </p>
            <p className="text-[15px] sm:text-base">
              Unser Anspruch ist einfach: Flächen, die wirklich frei sind. Nicht eine
              Schaufelspur im Schnee, sondern ein Gehweg auf nutzbarer Breite. Nicht pauschal
              Salz auf alles, sondern das Streumittel, das zum Belag passt. Und keine
              Versprechen, die im Januar nicht gehalten werden können.
            </p>
            <p className="text-[15px] sm:text-base">
              Was Sie bei uns nicht finden: erfundene Zertifikate, erfundene Mitarbeiterzahlen
              oder Bewertungen, die niemand geschrieben hat. Was Sie finden: eine klare
              Leistungsbeschreibung, ein objektbezogenes Angebot und einen Ansprechpartner,
              der erreichbar ist.
            </p>
          </div>

          <Reveal delay={0.12}>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {[
                { t: "Persönlich", d: "Ein Ansprechpartner über die gesamte Saison – ohne Zentrale." },
                { t: "Regional", d: "Fuhrpark-Start in Bremen-Hemelingen, Touren im Stadtgebiet." },
                { t: "Flächengerecht", d: "Gerät und Streumittel passend zu Belag und Nutzung." },
                { t: "Transparent", d: "Angebot auf Anfrage, klarer Leistungsumfang, keine Pauschalversprechen." },
              ].map((v) => (
                <div key={v.t} className="rounded-2xl border border-[#DCE5EF] bg-[#F5F8FC] p-6">
                  <p className="text-[16.5px] font-bold tracking-tight">{v.t}</p>
                  <p className="mt-2.5 text-[13.5px] leading-relaxed text-[#66788A]">{v.d}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-10 overflow-hidden rounded-2xl">
              <img
                src={IMG.clearedWalk}
                alt="Geräumter und gestreuter Gehweg im Winter"
                loading="lazy"
                className="h-64 w-full object-cover"
              />
            </div>
          </Reveal>
        </div>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <Reveal>
            <div className="relative overflow-hidden rounded-[22px] bg-[#062B52] p-7 text-white">
              <Stadtmusikanten className="pointer-events-none absolute -right-6 bottom-0 h-48 opacity-[0.1]" color="#FFFFFF" />
              <div className="relative z-10">
                <Label tone="orange">/ Inhaber</Label>
                <p className="mt-4 text-[24px] font-bold tracking-tight" style={{ color: "#fff" }}>
                  Adil Altun
                </p>
                <p className="mt-1.5 text-sm text-white/60">Inhaber & persönlicher Ansprechpartner</p>
                <ul className="mt-7 space-y-3 border-t border-white/15 pt-6">
                  <CheckRow light>Objektaufnahme vor Ort</CheckRow>
                  <CheckRow light>Räum- und Streuplanung</CheckRow>
                  <CheckRow light>Direkte Erreichbarkeit</CheckRow>
                </ul>
                <a href={BRAND.phoneHref} data-testid="about-phone" className="mt-7 flex items-center gap-3 text-base font-bold">
                  <Phone className="h-4 w-4 text-[#FF6B00]" /> {BRAND.phone}
                </a>
                <a href={BRAND.emailHref} data-testid="about-email" className="mt-3 flex items-center gap-3 break-all text-sm text-white/80">
                  <Mail className="h-4 w-4 text-[#FF6B00]" /> {BRAND.email}
                </a>
                <a
                  href={BRAND.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-whatsapp"
                  className="btn btn-wa mt-6 h-12 w-full text-sm"
                >
                  <MessageCircle className="h-4 w-4" /> Per WhatsApp kontaktieren
                </a>
                <Link to="/kontakt" data-testid="about-cta" className="btn btn-orange mt-2.5 h-12 w-full text-sm">
                  Winterdienst anfragen <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </aside>
      </div>
    </section>

    <CtaBand />
  </main>
);

export default UeberUns;
